import { useCallback, useMemo, useRef, useState } from 'react';
import { isDraftThought } from '../utils/draftPredicate';

/**
 * Card-stack queue for triaging drafts one at a time.
 *
 * Snapshots the set of drafts at first render and walks the user through
 * them. Skipping or marking-done removes the active id from the live queue
 * but the snapshot itself never grows — drafts created after entry are
 * deferred to the next session.
 *
 * @param {Array<Object>} thoughts - Full thoughts array. The active card is
 *   re-resolved from this array on every render so inline edits update live.
 * @param {(thought: Object) => boolean} [isDraft] - Predicate that selects
 *   drafts at mount time. Defaults to {@link isDraftThought}.
 *
 * @returns {{
 *   activeThought: Object|null,
 *   nextThought: Object|null,
 *   total: number,
 *   remaining: number,
 *   index: number,
 *   canMarkDone: boolean,
 *   isExhausted: boolean,
 *   skip: () => void,
 *   markDone: () => void,
 * }}
 */
const useDraftsQueue = (thoughts, isDraft = isDraftThought) => {
  // Snapshot draft IDs at first render so newly-created drafts don't
  // silently mutate the user's flow.
  const queueRef = useRef(null);
  if (queueRef.current === null) {
    queueRef.current = thoughts.filter(isDraft).map((t) => t.id);
  }
  const queue = queueRef.current;

  const [skipped, setSkipped] = useState(() => new Set());
  const [done, setDone] = useState(() => new Set());

  // One linear pass: build byId lookup AND filter the live queue at the
  // same time. Used downstream for active/next resolution.
  const { liveQueue, byId } = useMemo(() => {
    const map = new Map(thoughts.map((t) => [t.id, t]));
    const live = queue.filter(
      (id) => map.has(id) && !skipped.has(id) && !done.has(id)
    );
    return { liveQueue: live, byId: map };
  }, [queue, thoughts, skipped, done]);

  const activeId = liveQueue[0] || null;
  const nextId = liveQueue[1] || null;

  const activeThought = activeId ? byId.get(activeId) || null : null;
  const nextThought = nextId ? byId.get(nextId) || null : null;

  const total = queue.length;
  const remaining = liveQueue.length;
  const isExhausted = remaining === 0;
  // When exhausted, `total - remaining + 1 = total + 1` would overshoot;
  // pin to `total` so "Card N of M" never reads "Card 4 of 3".
  const index = total === 0 ? 0 : isExhausted ? total : total - remaining + 1;

  // Derive `canMarkDone` from `!isDraft` — single source of truth for the
  // predicate. Adding any classification graduates the thought naturally.
  const canMarkDone = activeThought ? !isDraft(activeThought) : false;

  const skip = useCallback(() => {
    if (!activeId) return;
    setSkipped((prev) => {
      const next = new Set(prev);
      next.add(activeId);
      return next;
    });
  }, [activeId]);

  const markDone = useCallback(() => {
    if (!activeId) return;
    setDone((prev) => {
      const next = new Set(prev);
      next.add(activeId);
      return next;
    });
  }, [activeId]);

  return {
    activeThought,
    nextThought,
    total,
    remaining,
    index,
    canMarkDone,
    isExhausted,
    skip,
    markDone,
  };
};

export default useDraftsQueue;
